// ============================================
// EXTERNAL CONNECTION FRAMEWORK (ECF) TYPES
// ============================================

// ----- Status / capability gating -----

export interface EcfConnection {
  id: number;
  connection_type: string;
  connector_key: string | null;
  provider_key: string;
  configuration_kind: 'yardi_mappings' | null;
  is_enabled: boolean;
  has_outbound: boolean;
  has_inbound: boolean;
}

export interface EcfStatus {
  ecf_enabled: boolean;
  connections: EcfConnection[];
  lumi_config_available: boolean;
}

// ----- Event ledger -----

export type EcfDirection = 'inbound' | 'outbound';
export type EcfEventCategory = 'successful' | 'processing' | 'attention' | 'informational';
export type EcfOverviewWindow = 'today' | '7d' | '30d';

export interface EcfEventFailure {
  code: string;
  title: string;
  explanation: string;
  remediation: string;
}

/**
 * A domain record a ledger row names, as the coordinator reads and links to it. Shared by the work
 * order and the service request enclosing it — they carry the same identity pair.
 */
export interface EcfEventEntityRef {
  uuid: string;
  number: string | null;
}

export interface EcfEvent {
  id: string;
  direction: EcfDirection;
  event_type: string | null;
  event_subtype: string | null;
  status: string | null;
  outcome: string | null;
  external_reference: string | null;
  delivery_disposition?: 'partner_call' | 'intentional_no_op' | 'shadow' | null;
  occurred_at: string | null;
  connection_id: number;
  attempts: number | null;
  category: EcfEventCategory;
  /** Backend-owned operator explanation for failed, dead-lettered, or quarantined events. */
  failure?: EcfEventFailure | null;
  /**
   * The anchored work order. Null when the event anchors none (an early inbound reject, or an
   * outbound row naming only a service request); optional so an older backend that does not yet
   * send the key renders as "none" rather than crashing.
   */
  work_order?: EcfEventEntityRef | null;
  /**
   * The service request enclosing the event — named directly by an outbound row, or inherited from
   * the parent of the work order. Same nullability contract as `work_order`.
   */
  service_request?: EcfEventEntityRef | null;
}

export interface EcfEventsMeta {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export interface EcfEventsResponse {
  data: EcfEvent[];
  meta: EcfEventsMeta;
}

/** Query params for GET events (server-side pagination + filtering). */
export interface EcfEventsQuery {
  direction?: EcfDirection | '';
  status?: string;
  event_type?: string;
  /**
   * Free-text search over the records an event names — a work order or service request number
   * prefix, or a pasted work order uuid. Min 3 chars server-side.
   */
  entity_search?: string;
  from?: string;
  to?: string;
  connection_id?: number | '';
  category?: EcfEventCategory | '';
  page?: number;
  per_page?: number;
}

// ----- Operational overview -----

export interface EcfOverviewWindowRange {
  key: EcfOverviewWindow;
  starts_at: string;
  ends_at: string;
}

export interface EcfOverviewCounts {
  total_events: number;
  inbound_events: number;
  outbound_events: number;
  successful: number;
  processing: number;
  attention_required: number;
  informational: number;
  partner_calls?: number;
  intentional_no_ops?: number;
  shadow_captures?: number;
  last_activity_at: string | null;
}

export interface EcfDocumentHealth {
  total: number;
  synced: number;
  processing: number;
  attention_required: number;
}

export interface EcfConnectionOverview extends EcfOverviewCounts {
  id: number;
  connection_type: string;
  connector_key: string | null;
  provider_key: string;
  is_enabled: boolean;
  has_inbound: boolean;
  has_outbound: boolean;
  document_health?: EcfDocumentHealth;
}

export interface EcfEventTypeOverview {
  event_type: string;
  total: number;
  inbound: number;
  outbound: number;
  last_activity_at: string | null;
}

export type EcfWorkflowOptionKey =
  | 'completed_under_nte'
  | 'declined_before_dispatch'
  | 'quote_declined_or_expired'
  | 'quote_approved_completed';

export interface EcfWorkflowWorkOrder {
  work_order_uuid: string | null;
  work_order_number: string | null;
  service_request_uuid: string | null;
  approval_requested_at: string | null;
  resolved_at: string | null;
  last_activity_at: string;
}

export interface EcfWorkflowOption {
  key: EcfWorkflowOptionKey;
  count: number;
  recent: EcfWorkflowWorkOrder[];
}

export interface EcfWorkflowDetail {
  count: number;
  recent: EcfWorkflowWorkOrder[];
}

export interface EcfWorkflowOverview {
  /** Work orders that requested external approval during the selected window. */
  total: number;
  /** Terminal work orders classified into one of the four supported outcomes. */
  resolved: number;
  /** Work orders still moving through an otherwise valid lifecycle. */
  active: number;
  /** Work orders whose history cannot be classified safely. */
  needs_review: number;
  options: EcfWorkflowOption[];
  active_details: EcfWorkflowDetail;
  needs_review_details: EcfWorkflowDetail;
}

export interface EcfOverview {
  window: EcfOverviewWindowRange;
  summary: EcfOverviewCounts;
  document_health?: EcfDocumentHealth;
  workflows: EcfWorkflowOverview;
  connections: EcfConnectionOverview[];
  top_event_types: EcfEventTypeOverview[];
  recent_events: EcfEvent[];
  recent_attention: EcfEvent[];
}

export type EcfOverviewResponse = Omit<EcfOverview, 'workflows'> & {
  /** Omitted by overview responses deployed before workflow aggregates. */
  workflows?: EcfWorkflowOverview | null;
};

// ----- Event detail (drawer) -----

export interface EcfEventAttempt {
  attempted_at: string | null;
  status: string | null;
  response_code: number | null;
  error: string | null;
}

export interface EcfSignatureMetadata {
  has_signature: boolean;
  // The value the HMAC was computed over: a unix epoch (inbound request-line) or an ISO
  // string, depending on lane — normalized for display by formatSignatureTimestamp.
  timestamp: string | number | null;
}

/**
 * The domain object an event concerns. This is what makes the lifecycle view possible: read
 * `work_order_uuid` off one event and re-query the ledger with it to get that entity's full
 * ECF history. `type` is null for events with no domain anchor (e.g. a connection test), and
 * the server also degrades it to null rather than surfacing another org's anchor.
 *
 * Optional on the interface because a detail response served before the entity block shipped
 * simply omits it — the drawer treats that as "no lifecycle available" rather than breaking.
 */
export interface EcfEventEntity {
  type: 'work_order' | 'service_request' | null;
  work_order_uuid: string | null;
  work_order_number: string | null;
  service_request_uuid: string | null;
}

export type EcfEventRecoveryState =
  | 'automatic'
  | 'available'
  | 'blocked'
  | 'reconciliation_required'
  | 'unavailable'
  | 'resolved';

export interface EcfEventRecovery {
  state: EcfEventRecoveryState;
  action: 'retry' | null;
  message: string;
  next_retry_at: string | null;
}

export interface EcfEventRetryResponse {
  status: 'queued' | 'automatic_retry' | 'already_resolved';
  message: string;
}

export interface EcfDeliveredPayload {
  /** False when no send-time copy exists. The recorded payload must NOT be shown in its place. */
  available: boolean;
  /** Why it is absent, when it is. */
  reason: 'not_recorded' | null;
  /** Already redacted server-side, like `payload`. Empty object when unavailable. */
  payload: Record<string, unknown>;
}

export interface EcfEventDetail {
  id: string;
  direction: EcfDirection;
  event_type: string | null;
  event_subtype: string | null;
  status: string | null;
  outcome: string | null;
  reason: string | null;
  /** Backend-owned explanation; raw reason remains for diagnostics and older deployments. */
  failure?: EcfEventFailure | null;
  external_reference: string | null;
  delivery_disposition?: 'partner_call' | 'intentional_no_op' | 'shadow' | null;
  occurred_at: string | null;
  connection_id: number;
  entity?: EcfEventEntity | null;
  /** Already redacted server-side — rendered read-only, never reconstructed. */
  payload: Record<string, unknown>;
  /**
   * Whether a body was ever persisted for this event. False on inbound rows: the inbound ops log
   * records identifiers only, so an empty `payload` there means "never stored", not "redacted
   * away". Optional for deployments whose backend predates the flag.
   */
  payload_recorded?: boolean;
  /**
   * What the partner ACTUALLY received, read from the send-time audit copy the worker writes.
   * It differs from `payload` by construction: the worker resolves media references into signed
   * URLs and, on a default webhook, re-stamps `external_reference` and drops its provenance
   * marker. Never reconstructed on read — a rebuild would run today's code over an old row and
   * could show a shape the partner never received — so rows from before the column, rows that
   * never reached the wire, and rows past retention report `available: false` and show nothing.
   * Optional for deployments whose backend predates the block.
   */
  delivered_payload?: EcfDeliveredPayload;
  /** Partner-supplied idempotency key — inbound only, shown when no body was recorded. */
  idempotency_key?: string | null;
  /** SHA-256 of the canonical inbound business fields — inbound only. */
  request_fingerprint?: string | null;
  signature_metadata: EcfSignatureMetadata;
  attempts: EcfEventAttempt[];
  /** Backend-owned recovery decision; absent on older deployments and inbound rows. */
  recovery?: EcfEventRecovery | null;
}

// ----- LuMi configuration -----

export type LumiConfigArea = 'theme' | 'availability' | 'access' | 'features' | 'blackout-dates';

export interface LumiThemeConfig {
  brand_primary: string;
}

export interface LumiTimeSlot {
  key: string;
  label: string;
  start: string;
  end: string;
  emergency_enabled?: boolean;
}

export interface LumiAvailabilityConfig {
  available_days: string[];
  time_slots: LumiTimeSlot[];
  advance_booking_days: number;
  max_booking_days: number;
}

export type LumiAuthMethod = 'phone' | 'last_name';

export interface LumiAccessConfig {
  auth_method: LumiAuthMethod;
  enabled_entry_types: string[];
  enabled_pet_types: string[];
  require_parking_info: boolean;
  support_phone?: string | null;
  /** Opaque LuMi-managed definitions that this editor must round-trip unchanged. */
  custom_entry_types: Record<string, unknown>[];
  /** Opaque LuMi-managed definitions that this editor must round-trip unchanged. */
  custom_pet_types: Record<string, unknown>[];
}

export interface LumiFeaturesConfig {
  enabled: string[];
}

export type BlackoutRecurrenceType = 'weekly' | 'monthly' | 'yearly';

export interface LumiBlackoutDate {
  date: string;
  reason?: string;
  is_recurring?: boolean;
  recurrence_type?: BlackoutRecurrenceType;
}

/**
 * Shape returned by GET lumi-config for the blackout area. The contract is
 * loosely pinned ("{dates:[...]} or similar — read defensively"), so this is
 * normalized on read (see normalizeBlackoutDates). The PUT body is the
 * distinct {upserts, deletes} shape below.
 */
export interface LumiBlackoutDatesConfig {
  dates?: LumiBlackoutDate[];
}

export interface LumiBlackoutDatesUpdate {
  upserts: LumiBlackoutDate[];
  deletes: string[];
}

export interface LumiConfig {
  theme: LumiThemeConfig | null;
  availability: LumiAvailabilityConfig | null;
  access: LumiAccessConfig | null;
  features: LumiFeaturesConfig | null;
  blackout_dates: LumiBlackoutDatesConfig | null;
  _errors?: Record<string, string>;
}

/** Field-level validation errors returned on a 422. */
export interface LumiConfigUpdateError {
  message?: string;
  errors?: Record<string, string[] | string>;
  /** Foresight's local FormRequest validation envelope. */
  error?: string | Record<string, string[] | string>;
  status?: number;
}

// ----- Yardi mapping configuration -----

export type EcfConfigurationKind = 'yardi_mappings';
export type YardiMappingSection =
  | 'lifecycle'
  | 'priority'
  | 'outbound-taxonomy'
  | 'inbound-taxonomy';

export type YardiCanonicalPriority = 'urgent' | 'high' | 'medium' | 'low';

export interface YardiCatalogStatus {
  label: string;
  kind: string;
}

export interface YardiCategoryPair {
  pair_key: string;
  category: string;
  subcategory: string | null;
}

export interface YardiCatalogReason {
  status: string | null;
  value: string;
  required: boolean;
}

export interface YardiMappingCatalog {
  digest: string | null;
  checked_at: string | null;
  expires_at: string | null;
  is_fresh: boolean;
  priorities: string[];
  statuses: YardiCatalogStatus[];
  category_pairs: YardiCategoryPair[];
  reasons: YardiCatalogReason[];
  resolutions: string[];
}

export interface YardiMappingReadiness {
  ready: boolean;
  proof_fresh: boolean;
  blockers: string[];
  required_status_total: number;
  required_status_mapped: number;
  priority_inbound_total: number;
  priority_inbound_mapped: number;
  priority_outbound_total: number;
  priority_outbound_mapped: number;
  outbound_total: number;
  outbound_mapped: number;
  inbound_total: number;
  inbound_mapped: number;
}

export interface YardiLifecycleMappings {
  required_status_keys: string[];
  intake_create_statuses: string[];
  status_mapping: Record<string, string | null>;
  reason_mapping: Record<string, string | null>;
  resolution_mapping: Record<string, string | null>;
}

export interface YardiPriorityMappings {
  inbound_mapping: Record<string, YardiCanonicalPriority>;
  outbound_mapping: Record<YardiCanonicalPriority, string | null>;
  emergency_priorities: string[];
}

export interface YardiOutboundMapping {
  pair_key: string;
  category: string;
  subcategory: string | null;
}

export interface YardiOutboundTaxonomyRow {
  problem_diagnosis_uuid: string;
  category: string;
  subcategory: string;
  diagnosis: string;
  mapping: YardiOutboundMapping | null;
}

export interface YardiInboundMapping {
  problem_diagnosis_uuid: string;
  category: string;
  subcategory: string;
  diagnosis: string;
}

export interface YardiInboundTaxonomyRow extends YardiCategoryPair {
  is_stale?: boolean;
  mapping: YardiInboundMapping | null;
}

export interface YardiMappingsConnection {
  id: number;
  connection_type: string;
  connector_key: string | null;
  provider_key: string;
  is_enabled: boolean;
  configuration_kind: EcfConfigurationKind;
}

export interface YardiHeldOutboundWork {
  delivery_attempt_id: number;
  event_uuid: string;
  event_type: string | null;
  reason: string;
  status: 'quarantined';
  held_at: string | null;
}

export interface YardiHeldInboundWork {
  ingestion_receipt_id: number;
  item_identity_hash: string;
  reason: string;
  status: 'quarantined' | 'failed' | 'pending';
  held_at: string | null;
}

export interface YardiHeldWorkCollection<T> {
  items: T[];
  has_more: boolean;
}

export interface YardiHeldWork {
  outbound: YardiHeldWorkCollection<YardiHeldOutboundWork>;
  inbound: YardiHeldWorkCollection<YardiHeldInboundWork>;
}

export interface YardiMappingsConfig {
  connection: YardiMappingsConnection;
  settings_version: string;
  catalog: YardiMappingCatalog;
  readiness: YardiMappingReadiness;
  lifecycle: YardiLifecycleMappings;
  priority: YardiPriorityMappings;
  outbound_taxonomy: { rows: YardiOutboundTaxonomyRow[] };
  inbound_taxonomy: { rows: YardiInboundTaxonomyRow[] };
  held_work: YardiHeldWork;
}

type YardiPriorityReadinessKey =
  | 'priority_inbound_total'
  | 'priority_inbound_mapped'
  | 'priority_outbound_total'
  | 'priority_outbound_mapped';

/**
 * Rolling deployments can return the pre-priority mapping document until the
 * backend rollout reaches a tenant. The Redux slice normalizes this transitional
 * response before exposing it to mapping screens.
 */
export type YardiMappingsConfigResponse = Omit<
  YardiMappingsConfig,
  'catalog' | 'readiness' | 'priority'
> & {
  catalog: Omit<YardiMappingCatalog, 'priorities'> & { priorities?: string[] };
  readiness: Omit<YardiMappingReadiness, YardiPriorityReadinessKey> &
    Partial<Pick<YardiMappingReadiness, YardiPriorityReadinessKey>>;
  priority?: YardiPriorityMappings;
};

export type YardiMappingRedriveDirection = 'inbound' | 'outbound';

export interface YardiMappingRedriveResponse {
  status: 'queued';
}

export interface YardiMappingUpdateBase {
  settings_version: string;
  catalog_digest: string;
}

export type YardiLifecycleUpdate = YardiMappingUpdateBase & YardiLifecycleMappings;

export type YardiPriorityUpdate = YardiMappingUpdateBase & YardiPriorityMappings;

export interface YardiOutboundTaxonomyUpdate extends YardiMappingUpdateBase {
  mappings: Record<string, YardiOutboundMapping>;
}

export interface YardiInboundTaxonomyUpdate extends YardiMappingUpdateBase {
  mappings: Record<
    string,
    Pick<YardiInboundMapping, 'problem_diagnosis_uuid'> &
      Pick<YardiCategoryPair, 'category' | 'subcategory'>
  >;
}

export type YardiMappingUpdate =
  | YardiLifecycleUpdate
  | YardiPriorityUpdate
  | YardiOutboundTaxonomyUpdate
  | YardiInboundTaxonomyUpdate;

export interface YardiMappingUpdateError {
  code?:
    | 'settings_version_conflict'
    | 'catalog_digest_conflict'
    | 'discovery_inputs_changed'
    | 'mapping_validation_failed'
    | string;
  message?: string;
  errors?: Record<string, string[] | string>;
  error?: string | Record<string, string[] | string>;
  status?: number;
}

// ----- Slice state -----

export interface ExternalConnectionsState {
  status: EcfStatus | null;
  statusLoading: boolean;
  statusError: string | null;

  events: EcfEvent[];
  eventsMeta: EcfEventsMeta | null;
  eventsLoading: boolean;
  eventsError: string | null;

  overview: EcfOverview | null;
  overviewLoading: boolean;
  overviewError: string | null;

  eventDetail: EcfEventDetail | null;
  eventDetailLoading: boolean;
  eventDetailError: string | null;

  /**
   * The open event's entity lifecycle. DELIBERATELY separate from `events`, which the ledger
   * page owns: the drawer opens on top of that list, so writing the lifecycle into `events`
   * would replace the page's rows underneath it.
   */
  lifecycle: EcfEvent[];
  lifecycleLoading: boolean;
  lifecycleError: string | null;
  /** The work order `lifecycle` was loaded for, so the drawer refetches only when it changes. */
  lifecycleWorkOrderUuid: string | null;
  /**
   * Server-reported total for the entity. The timeline loads one capped page, so this is what
   * lets the UI say "showing 50 of 120" instead of silently implying the history is complete.
   */
  lifecycleTotal: number;

  lumiConfig: LumiConfig | null;
  lumiConfigLoading: boolean;
  lumiConfigError: string | null;

  yardiMappings: YardiMappingsConfig | null;
  yardiMappingsLoading: boolean;
  yardiMappingsError: string | null;
}

export const initialState: ExternalConnectionsState = {
  status: null,
  statusLoading: false,
  statusError: null,

  events: [],
  eventsMeta: null,
  eventsLoading: false,
  eventsError: null,

  overview: null,
  overviewLoading: false,
  overviewError: null,

  eventDetail: null,
  eventDetailLoading: false,
  eventDetailError: null,

  lifecycle: [],
  lifecycleLoading: false,
  lifecycleError: null,
  lifecycleWorkOrderUuid: null,
  lifecycleTotal: 0,

  lumiConfig: null,
  lumiConfigLoading: false,
  lumiConfigError: null,

  yardiMappings: null,
  yardiMappingsLoading: false,
  yardiMappingsError: null,
};
